// src/hooks/useStudents.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * useStudents — keeps your original stateful API and adds a joined, display-ready
 * getStudentEnrollments() used by StudentDetails (instant UX compatible).
 */

const toBool = (v) => {
  if (v === true || v === false) return v;
  if (v === 'true' || v === '1' || v === 1) return true;
  if (v === 'false' || v === '0' || v === 0 || v === '' || v == null) return false;
  return !!v;
};

export const useStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  /* --------------------------------- LIST --------------------------------- */
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('students')
        .select('*')
        .eq('is_deleted', false) // Only fetch non-deleted students
        .order('full_name', { ascending: true });

      if (error) throw error;
      setStudents(data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching students:', err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  /* ------------------------------- CREATE --------------------------------- */
  const addStudent = async (studentData) => {
    try {
      const { enrollments, ...studentInfo } = studentData;

      const university_degree = studentInfo.university_degree ?? null;
      const mqa_recognition   = toBool(studentInfo.mqa_recognition ?? false);
      const moodle_access     = toBool(studentInfo.moodle_access ?? false);
      const misc_remarks      = studentInfo.misc_remarks ?? null;
      // NEW: normalize membership_status (frontend-enforced values)
      const membership_status = (studentInfo.membership_status && String(studentInfo.membership_status).trim()) || 'Pending';

      const formattedStudentData = {
        ...studentInfo,
        university_degree,
        mqa_recognition,
        moodle_access,
        misc_remarks,
        membership_status, // NEW
        full_name: (studentInfo.full_name || '').toUpperCase(),
        address: (studentInfo.address || '').toUpperCase(),
        is_deleted: false,
      };

      const { data: studentResult, error: studentError } = await supabase
        .from('students')
        .insert([formattedStudentData])
        .select();

      if (studentError) throw studentError;

      const newStudent = studentResult?.[0];

      if (newStudent && Array.isArray(enrollments) && enrollments.length > 0) {
        const rows = enrollments.map((enrollment) => ({
          student_id: newStudent.id,
          subject_id: enrollment.subjectId,
          intake_id:  enrollment.intakeId,
          enrollment_date: enrollment.enrollmentDate,
          status: enrollment.status,
          is_deleted: false,
        }));

        const { error: enError } = await supabase.from('enrollments').insert(rows);
        if (enError) {
          await supabase.from('students').update({ is_deleted: true }).eq('id', newStudent.id);
          throw new Error('Student created but enrollments failed. Operation rolled back.');
        }
      }

      setStudents((prev) => [...prev, newStudent]);
      return { success: true, data: newStudent };
    } catch (err) {
      setError(err.message);
      console.error('Error adding student with enrollments:', err);
      return { success: false, error: err.message };
    }
  };

  /* -------------------------------- UPDATE -------------------------------- */
  const updateStudent = async (id, updates) => {
    try {
      const { enrollments, ...studentUpdates } = updates;

      const normalizedUpdates = { ...studentUpdates };
      if ('university_degree' in studentUpdates) {
        normalizedUpdates.university_degree = studentUpdates.university_degree ?? null;
      }
      if ('mqa_recognition' in studentUpdates) {
        normalizedUpdates.mqa_recognition = toBool(studentUpdates.mqa_recognition);
      }
      if ('moodle_access' in studentUpdates) {
        normalizedUpdates.moodle_access = toBool(studentUpdates.moodle_access);
      }
      if ('misc_remarks' in studentUpdates) {
        normalizedUpdates.misc_remarks = studentUpdates.misc_remarks ?? null;
      }
      // NEW: allow updating membership_status
      if ('membership_status' in studentUpdates) {
        normalizedUpdates.membership_status =
          (studentUpdates.membership_status && String(studentUpdates.membership_status).trim()) || 'Pending';
      }

      const formattedUpdates = {
        ...normalizedUpdates,
        ...(normalizedUpdates.full_name
          ? { full_name: (normalizedUpdates.full_name || '').toUpperCase() }
          : {}),
        ...(normalizedUpdates.address
          ? { address: (normalizedUpdates.address || '').toUpperCase() }
          : {}),
      };

      const { data, error } = await supabase
        .from('students')
        .update(formattedUpdates)
        .eq('id', id)
        .select();

      if (error) throw error;

      const updated = data?.[0];
      setStudents((prev) => prev.map((s) => (s.id === id ? updated : s)));
      return { success: true, data: updated };
    } catch (err) {
      setError(err.message);
      console.error('Error updating student:', err);
      return { success: false, error: err.message };
    }
  };

  /* -------------------------------- DELETE -------------------------------- */
  const deleteStudent = async (id) => {
    try {
      // 1) Student (soft delete)
      const { error: studentErr } = await supabase
        .from('students')
        .update({ is_deleted: true })
        .eq('id', id);
      if (studentErr) throw studentErr;

      // 2) Enrollments (soft delete)
      const { error: enrollErr } = await supabase
        .from('enrollments')
        .update({ is_deleted: true })
        .eq('student_id', id);
      if (enrollErr) throw enrollErr;

      // 3) Payments (soft delete)
      const { error: payErr } = await supabase
        .from('payments')
        .update({ is_deleted: true })
        .eq('student_id', id);
      if (payErr) throw payErr;

      // 4) Student Documents (soft delete)
      const { error: docErr } = await supabase
        .from('student_documents')
        .update({ is_deleted: true })
        .eq('student_id', id);
      if (docErr) throw docErr;

      // 5) Local state
      setStudents((prev) => prev.filter((s) => s.id !== id));

      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error deleting student:', err);
      return { success: false, error: err.message };
    }
  };


  /* ----------------------- STUDENT ENROLLMENTS (JOINED) -------------------- */
  const getStudentEnrollments = useCallback(async (studentId) => {
    try {
      if (!studentId) return { success: true, data: [] };

      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          id, student_id, subject_id, intake_id, enrollment_date, status, is_deleted,
          intake:intakes (id, name, start_date, end_date),
          subject:subjects (id, code, name)
        `)
        .eq('student_id', studentId)
        .eq('is_deleted', false)
        .order('start_date', { foreignTable: 'intakes', ascending: false })
        .order('code',       { foreignTable: 'subjects', ascending: true })
        .order('name',       { foreignTable: 'subjects', ascending: true });

      if (error) throw error;

      const sorted = (data || []).slice().sort((a, b) => {
        const aStart = a?.intake?.start_date ? new Date(a.intake.start_date) : new Date(0);
        const bStart = b?.intake?.start_date ? new Date(b.intake.start_date) : new Date(0);
        if (bStart - aStart !== 0) return bStart - aStart;

        const ac = a?.subject?.code || '';
        const bc = b?.subject?.code || '';
        if (ac.localeCompare(bc) !== 0) return ac.localeCompare(bc);

        const an = a?.subject?.name || '';
        const bn = b?.subject?.name || '';
        return an.localeCompare(bn);
      });

      return { success: true, data: sorted };
    } catch (err) {
      console.error('Error fetching student enrollments:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /* ---------------------- ENROLLMENT HELPERS (OPTIONAL) -------------------- */
  const createEnrollment = async (enrollmentData) => {
    try {
      const { data, error } = await supabase
        .from('enrollments')
        .insert([
          {
            student_id: enrollmentData.studentId,
            subject_id: enrollmentData.subjectId,
            intake_id:  enrollmentData.intakeId,
            enrollment_date: enrollmentData.enrollmentDate,
            status: enrollmentData.status,
            is_deleted: false,
          },
        ])
        .select(`
          id, student_id, subject_id, intake_id, enrollment_date, status, is_deleted,
          intake:intakes (id, name, start_date, end_date),
          subject:subjects (id, code, name)
        `);

      if (error) throw error;
      return { success: true, data: data?.[0] || null };
    } catch (err) {
      setError(err.message);
      console.error('Error creating enrollment:', err);

      if (err.code === '23505' && err.message?.includes('enrollments_student_id_subject_id_intake_id_key')) {
        return {
          success: false,
          error: 'This student is already enrolled in this subject for the specified intake.',
        };
      }
      if (err.code === '23503') {
        return {
          success: false,
          error: 'Referenced data not found. Please check if the student, subject, and intake exist.',
        };
      }
      return { success: false, error: err.message };
    }
  };

  const deleteEnrollment = async (enrollmentId) => {
    try {
      const { error } = await supabase
        .from('enrollments')
        .update({ is_deleted: true })
        .eq('id', enrollmentId);

      if (error) throw error;
      return { success: true };
    } catch (err) {
      setError(err.message);
      console.error('Error deleting enrollment:', err);
      return { success: false, error: err.message };
    }
  };

  /* -------------------------------- RETURN -------------------------------- */
  return {
    students,
    loading,
    error,
    addStudent,
    updateStudent,
    deleteStudent,
    getStudentEnrollments, // now stable
    createEnrollment,
    deleteEnrollment,
    refetch: fetchStudents, // stable too
  };
};
